import { useTranslation } from "react-i18next";
import { useState } from "react";
import { useUser } from "@clerk/clerk-react";
import { useMutation } from "@tanstack/react-query";
import { MessageSquare } from "lucide-react";
import { getUnsafeMetadata } from "@shared/types/clerk-metadata";
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogDescription,
  AlertDialogFooter,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { submitSmsConsent } from "@/requests/internal-sms/sms-consent";

export function SmsConsentDialog() {
  const { t } = useTranslation();
  const { user, isLoaded } = useUser();
  const [dismissed, setDismissed] = useState(false);

  const mutation = useMutation({
    mutationFn: submitSmsConsent,
    onSuccess: () => {
      setDismissed(true);
      user?.reload();
    },
  });

  if (!isLoaded || !user) return null;

  const smsConsent = getUnsafeMetadata(user).smsConsent;

  if (smsConsent || dismissed) return null;

  const handleAccept = () => mutation.mutate(true);
  const handleDecline = () => mutation.mutate(false);

  return (
    <AlertDialog open onOpenChange={() => {}}>
      <AlertDialogContent onEscapeKeyDown={(e) => e.preventDefault()}>
        <AlertDialogHeader>
          <div className="flex items-center gap-3 mb-2">
            <div className="p-2 bg-primary/10 rounded-lg">
              <MessageSquare className="h-6 w-6 text-primary" />
            </div>
            <AlertDialogTitle>{t("components.smsConsentDialog.stayInTheLoop")}</AlertDialogTitle>
          </div>
          <AlertDialogDescription className="text-left space-y-3">
            <span className="block">
              {t("components.smsConsentDialog.weAposDLike")}
            </span>
            <span className="block text-xs">
              {t("components.smsConsentDialog.standardMessagingRatesMay")}
            </span>
          </AlertDialogDescription>
        </AlertDialogHeader>

        {mutation.isError && (
          <p className="text-sm text-destructive">
            {t("components.smsConsentDialog.somethingWentWrongPlease")}
          </p>
        )}

        <AlertDialogFooter>
          <Button
            variant="outline"
            onClick={handleDecline}
            disabled={mutation.isPending}
          >
            {t("components.smsConsentDialog.noThanks")}
          </Button>
          <Button
            onClick={handleAccept}
            disabled={mutation.isPending}
          >
            {mutation.isPending ? t("components.smsConsentDialog.oneMoment") : t("components.smsConsentDialog.iAgree")}
          </Button>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
